import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import {
	OutgoingLinksQuery,
	type OutgoingLinksQueryData,
	type OutgoingLinksQueryVariables,
} from '@confluence/connectors/entry-points/preloadOutgoingLinks.ts';
import {
	getThirdPartyPrompt,
	type ThirdPartyPromptResult,
} from '@confluence/experiment-related-content-connections/entry-points/getThirdPartyPrompt.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

const NO_PROMPT: ThirdPartyPromptResult = {
	state: { kind: 'none' },
	count: 0,
	diagnostics: {
		connectorRowsFailureReason: null,
		connectorRowCount: 0,
		candidateCount: 0,
		addressCount: 0,
		unmatchedAddressCount: 0,
	},
};

const readAddresses = (data: OutgoingLinksQueryData | undefined): string[] =>
	data?.content?.nodes?.[0]?.outgoingLinks?.externalOutgoingLinks?.nodes
		?.map((node) => node?.url)
		.filter((url): url is string => Boolean(url)) ?? [];

/**
 * Whether the lowest-ranked card should become a connect prompt. Synchronous on both sides of
 * hydration: the query is read from the cache the route warmed, and detection is a pure call.
 */
export const useThirdPartyPromptState = (contentId: string): ThirdPartyPromptResult => {
	const { cloudId, userId } = useSessionData();

	const { data } = useQueryInterop<OutgoingLinksQueryData, OutgoingLinksQueryVariables>(
		OutgoingLinksQuery,
		{
			variables: { contentId },
			fetchPolicy: 'cache-only',
			skip: !contentId || !userId,
		},
	);

	if (!userId) {
		return NO_PROMPT;
	}

	// A plain function, not a hook, so it may be skipped. React Compiler caches it for this component.
	return getThirdPartyPrompt({ userId, cloudId, pageAddresses: readAddresses(data) });
};
